import { useCallback, useState } from "react";
// libraries
import { useTranslation } from "react-i18next";
import { Button, styled } from "@mui/material";
import { useForm } from "react-hook-form";
// utils
import { getBaseDomain, replaceSpecialCharacters } from "../utils/domainUtils";
import { ThemeTextField } from "./sample/ThemeTextField";
import { Wrapper } from "./sample/Wrapper";
import ShiawaLogo from "../assets/ShiawaLogo.png";
import { useStore } from "../utils/useStore";

const MainWrapper = styled("div")(({ theme }) => ({
  minHeight: "100vh",
  backgroundColor: "#fff !important",
  "& a": {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      color: "#aaa",
    },
  },
}));

const SubdomainWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  "& span": {
    margin: theme.spacing(1, 0, 1, 1),
  },
}));

const Subdomain = styled("div")(({ theme }) => ({
  backgroundColor: "lightgrey",
  padding: theme.spacing(3),
  borderRadius: 4,
  textAlign: "center",
  fontSize: 20,
}));

const MainButton = styled(Button)(({ theme }) => ({
  color: "white",
  fontWeight: "bold",
  borderRadius: 4,
  outline: "2px solid transparent",
  outlineOffset: 2,
  margin: theme.spacing(1, 0.5, 0.5, 0),
  marginTop: theme.spacing(1) + "!important",
  width: "100%",
  "transition-property": "all",
  "transition-timing-function": "cubic-bezier(0.4, 0, 0.2, 1)",
  "transition-duration": "150ms",
  "&:active": {
    backgroundColor: "rgba(71, 85, 105, 1)",
  },
  "&:focus": {
    outline: "none",
  },
}));

const Footer = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: theme.spacing(2),
  fontSize: "75%",
  alignItems: "center",
  justifyContent: "center",
  "& img": {
    height: "12px",
  },
  "& #footerText": {
    padding: theme.spacing(0, 1),
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },
}));

const SignupLink = styled("p")(() => ({
  textAlign: "center",
}));

const signupLink = "https://get-started." + process.env.REACT_APP_BASE_DOMAIN;

export const SubdomainStep = () => {
  const { t } = useTranslation();
  const { subdomain, updateSubdomain } = useStore();

  const [loading, setLoading] = useState(false);

  const {
    register,
    formState: { errors },
    setValue,
    handleSubmit,
    clearErrors,
    setError,
  } = useForm();

  const handleChangeSubdomain = useCallback(
    (e) => {
      const newValue = replaceSpecialCharacters(e.target.value);
      setValue("subdomain", newValue);
      clearErrors("subdomain");
    },
    [setValue, clearErrors]
  );

  const onSubmit = useCallback(
    ({ subdomain: subdomainValue }) => {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_BASEURL}/check-subdomain/${subdomainValue}`)
        .then((data) => data.json())
        .then((data) => {
          if (data["hydra:statusCode"] === 403) {
            const newDomain = subdomainValue + "." + getBaseDomain();
            updateSubdomain(newDomain);
          } else {
            setError("subdomain", { type: "custom", message: t("invalidSubdomain") });
          }
        })
        .finally(() => setLoading(false));
    },
    [setError, t, updateSubdomain]
  );

  return (
    <MainWrapper>
      <Wrapper title={t(subdomain ? "settingsHeader" : "subdomainHeader")}>
        {subdomain ? (
          <div>
            <Subdomain>{subdomain}</Subdomain>
            <MainButton variant="contained" disabled={loading} onClick={() => updateSubdomain("")}>
              {t("logout")}
            </MainButton>
          </div>
        ) : (
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <SubdomainWrapper>
                <ThemeTextField
                  label={t("subdomain")}
                  name="subdomain"
                  validationRules={{
                    required: { value: true, message: t("validation.required") },
                    minLength: { value: 6, message: t("validation.subdomain.minLength") },
                  }}
                  {...{ register, errors }}
                  onChange={handleChangeSubdomain}
                />
                <span>.{getBaseDomain()}</span>
              </SubdomainWrapper>
              <MainButton variant="contained" type="submit" disabled={loading}>
                {t("next")}
              </MainButton>
            </form>
          </div>
        )}
        <SignupLink>
          {t("signup1")}
          <a href={signupLink} target="_blank" rel="noopener noreferrer">
            {t("signup2")}
          </a>
          {t("signup3")}
        </SignupLink>
        <Footer>
          <a href="https://shiawa.com" target="_blank" rel="noopener noreferrer">
            <img src={ShiawaLogo} alt="logo" />
          </a>
          <div id="footerText">· ©Shiawa. All rights reserved ·</div>
          <a href="https://shiawa.com" target="_blank" rel="noopener noreferrer">
            shiawa.com
          </a>
        </Footer>
      </Wrapper>
    </MainWrapper>
  );
};
